import { Dialog, Transition, Combobox } from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import { ragAsk, sendEvent } from "../../service/ragSearch";
import { ColorRing } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import formatTimestamp from "../../ReusableFunctions/formatTimestamp";
import RagRecurring from "../../ReuseableComponents/rag/RagRecurring";

export default function SearchModal({ searchOpen, setSearchOpen }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [moments, setMoments] = useState([]); // flattened {group, item}
  const [recurring, setRecurring] = useState(null);
  const [kind, setKind] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [selected, setSelected] = useState({});

  function closeModal() {
    setSearchOpen(false);
    setSelected({});
  }

  useEffect(() => {
    if (query.trim() === "") {
      setLoading(false);
      setMoments([]);
      setRecurring(null);
      setKind(null);
      return;
    }
    setLoading(true);
    let ignore = false; // guards against a stale in-flight request resolving last
    const timer = setTimeout(() => {
      ragAsk(query)
        .then((data) => {
          if (ignore) return;
          setLoading(false);
          setKind(data.type);
          setRequestId(data.request_id);
          if (data.type === "recurring") {
            setRecurring(data);
            setMoments([]);
            return;
          }
          setRecurring(null);
          const flat = [];
          (data.results?.facets || []).forEach((facet) => {
            (facet.groups || []).forEach((group) => {
              (group.items || []).forEach((item) => flat.push({ group, item }));
            });
          });
          setMoments(flat.slice(0, 5));
        })
        .catch(() => {
          if (ignore) return;
          setLoading(false);
          setMoments([]);
        });
    }, 300);
    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [query]);

  const handleSelectMoment = (entry) => {
    if (!entry || !entry.item) return;
    setSelected(entry);
    if (requestId) {
      sendEvent({ request_id: requestId, kind: "click", class_id: entry.group.class_id });
    }
    closeModal();
    if (!entry.group.permalink) return;
    const seconds = Math.floor((entry.item.start_ms || 0) / 1000);
    navigate(`/${entry.group.permalink}?t=${seconds}&src=${encodeURIComponent(entry.item.url)}`);
  };

  const goToFullSearch = () => {
    closeModal();
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  return (
    <Transition appear show={searchOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[99999]" onClose={closeModal}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-[600px] transform rounded-lg bg-whiteGrey text-left align-middle shadow-xl transition-all">
                <div className="rounded-lg md:bg-opacity-[0.4] py-1">
                  <Combobox as="div" value={selected} onChange={handleSelectMoment}>
                    <div className="flex items-center gap-x-2 w-full px-4 py-2">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                      </svg>
                      <Combobox.Input
                        onChange={(e) => setQuery(e.target.value)}
                        displayValue={() => query}
                        placeholder="search..."
                        className="block w-full bg-transparent outline-none border-0 py-1.5 text-primaryDark focus:border-transparent sm:text-lg sm:leading-normal font-semibold"
                      />
                    </div>

                    <Combobox.Options static className="py-2 px-4 border-t-[1px] border-[#D2D6DC]">
                      {loading ? (
                        <div className="flex justify-center">
                          <ColorRing visible={true} height="80" width="80" ariaLabel="blocks-loading" wrapperStyle={{}} wrapperClass="blocks-wrapper" colors={["#D2D6DC", "#D2D6DC", "#D2D6DC", "#D2D6DC", "#D2D6DC"]} />
                        </div>
                      ) : query.trim() === "" ? null : kind === "recurring" ? (
                        <div className="py-2" onClick={closeModal}>
                          <RagRecurring recurring={recurring} />
                        </div>
                      ) : moments.length === 0 ? (
                        <div className="flex justify-center py-6">No data found</div>
                      ) : (
                        <>
                          {kind === "answer" && (
                            <div onClick={goToFullSearch} className="cursor-pointer py-2 pl-4 text-primaryBlue font-bold">
                              Get full answer →
                            </div>
                          )}
                          {moments.map((entry, idx) => (
                            <Combobox.Option
                              key={`${entry.group.class_id}-${entry.item.start_ms}-${idx}`}
                              value={entry}
                              className={({ active }) => `relative cursor-pointer select-none py-2 pl-4 pr-4 ${active ? "bg-primaryBlue text-whiteGrey" : "text-primaryDark"}`}
                            >
                              <div className="font-semibold">{entry.group.title}</div>
                              <div className="text-sm opacity-80">
                                {formatTimestamp(entry.item.start_ms)} · {entry.item.snippet}
                              </div>
                            </Combobox.Option>
                          ))}
                        </>
                      )}
                    </Combobox.Options>

                    <div className="mt-4 flex justify-center gap-x-10 mb-6 items-center">
                      <button type="button" className="w-[146px] md:w-[240px] h-[45px] py-2 bg-transparent border-[1px] border-primaryBlue text-primaryBlue rounded-[61px] transition duration-300" onClick={closeModal}>
                        Cancel
                      </button>
                      <button type="button" onClick={goToFullSearch} className="w-[146px] md:w-[240px] h-[45px] py-2 text-white bg-primaryBlue hover:bg-opacity-[0.9] focus:bg-primaryBlue rounded-[61px] transition duration-300 disabled:opacity-50">
                        Search
                      </button>
                    </div>
                  </Combobox>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
